'use client'

import { m, useInView } from 'framer-motion'
import {
  ArrowRight,
  Code2,
  Cpu,
  type LucideIcon,
  MessageSquare,
  Palette,
  Rocket,
  Search,
  Sparkles,
} from 'lucide-react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import type { ComponentType, CSSProperties } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { getDirectionTheme } from '@/lib/directionTheme'
import type { ServiceDTO } from '@/types/services'

type ServicesSplitSceneProps = {
  activeId: string
  play?: boolean
}

type ConnectionInfo = {
  saveData?: boolean
  effectiveType?: string
}

type NavigatorWithPerformanceHints = Navigator & {
  connection?: ConnectionInfo
  mozConnection?: ConnectionInfo
  webkitConnection?: ConnectionInfo
  deviceMemory?: number
  hardwareConcurrency?: number
}

function shouldEnable3DScene() {
  if (typeof window === 'undefined') return false
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false

  const nav = navigator as NavigatorWithPerformanceHints
  const connection = nav.connection ?? nav.mozConnection ?? nav.webkitConnection

  if (connection?.saveData) return false
  if (connection?.effectiveType && /(^slow-2g$|^2g$|^3g$)/.test(connection.effectiveType))
    return false

  const deviceMemory = nav.deviceMemory ?? 4
  const hardwareConcurrency = nav.hardwareConcurrency ?? 4
  return deviceMemory >= 4 && hardwareConcurrency >= 4
}

function LazyServicesSplitScene({ activeId, play }: ServicesSplitSceneProps) {
  const [SceneComponent, setSceneComponent] =
    useState<ComponentType<ServicesSplitSceneProps> | null>(null)

  useEffect(() => {
    if (!play || SceneComponent || !shouldEnable3DScene()) return

    let cancelled = false
    void import('@/components/canvas/ServicesSplitScene').then((mod) => {
      if (!cancelled) setSceneComponent(() => mod.default)
    })

    return () => {
      cancelled = true
    }
  }, [play, SceneComponent])

  if (!play || !SceneComponent) {
    return <div className="h-full w-full opacity-0" />
  }

  return <SceneComponent activeId={activeId} play={play} />
}

const ICON_BY_SLUG: Record<string, LucideIcon> = {
  development: Code2,
  'ai-agents': Cpu,
  design: Palette,
  smm: MessageSquare,
  marketing: Rocket,
  seo: Search,
}

function compactLabel(text: string) {
  return text.split(/\s+/).filter(Boolean).slice(0, 4).join(' ')
}

function getProofLine(benefits: string[] | undefined) {
  if (Array.isArray(benefits) && benefits.length > 0) {
    return benefits.slice(0, 2).map(compactLabel).join(' • ')
  }
  return 'Фокус на конверсии • Запуск под KPI'
}

function DirectionContent({ data, isLast }: { data: ServiceDTO; isLast?: boolean }) {
  const theme = getDirectionTheme(data.slug)
  const sceneRef = useRef<HTMLDivElement | null>(null)
  const cardsGridRef = useRef<HTMLDivElement | null>(null)
  const sceneInView = useInView(sceneRef, {
    amount: 0.35,
    margin: '260px 0px 260px 0px',
  })
  const [maxCopyHeight, setMaxCopyHeight] = useState(0)
  const router = useRouter()

  const prefetchRoute = (href: string) => {
    try {
      router.prefetch(href)
    } catch {
      // no-op
    }
  }

  useEffect(() => {
    const root = cardsGridRef.current
    if (!root) return

    let frame = 0
    const measure = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        const blocks = Array.from(root.querySelectorAll<HTMLElement>('[data-card-copy-inner]'))
        if (!blocks.length) return
        const next = blocks.reduce((max, node) => Math.max(max, node.offsetHeight), 0)
        setMaxCopyHeight((prev) => (prev === next ? prev : next))
      })
    }

    measure()

    const observer = new ResizeObserver(() => measure())
    observer.observe(root)
    root
      .querySelectorAll<HTMLElement>('[data-card-copy-inner]')
      .forEach((node) => observer.observe(node))
    window.addEventListener('resize', measure)

    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [data.slug, data.subServices])

  return (
    <section
      id={`service-section-${data.slug}`}
      data-service-slug={data.slug}
      className={`relative scroll-mt-40 ${isLast ? 'pt-10 pb-0 md:pt-14 md:pb-0' : 'py-10 md:py-14'}`}
    >
      <m.div
        initial={{ opacity: 0, y: 22 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.22 }}
        transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
        className="relative mb-7 overflow-hidden rounded-[28px] border border-white/15 bg-gradient-to-br from-[#0c1220]/95 to-[#0a1020]/90 backdrop-blur-2xl md:mb-9"
      >
        <div className="absolute -top-24 -left-20 h-72 w-72 rounded-full bg-blue-500/15 blur-[90px]" />
        <div className="absolute -right-20 -bottom-24 h-72 w-72 rounded-full bg-purple-500/15 blur-[90px]" />
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_bottom,rgba(255,255,255,0.08),transparent_35%)]" />

        <div className="relative z-10 p-5 md:p-7 lg:p-8">
          <div className="grid grid-cols-1 items-center gap-6 md:grid-cols-[230px,1fr] md:gap-8 xl:grid-cols-[260px,1fr]">
            <div
              ref={sceneRef}
              className="relative h-[220px] overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] md:h-[240px]"
            >
              <m.div
                initial={{ opacity: 0, scale: 0.97 }}
                animate={sceneInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="absolute inset-0"
              >
                <LazyServicesSplitScene activeId={data.slug} play={sceneInView} />
              </m.div>

              <m.div
                initial={{ opacity: 1 }}
                animate={sceneInView ? { opacity: 0 } : { opacity: 1 }}
                transition={{ duration: 1.05, delay: 0.15, ease: 'easeOut' }}
                className="pointer-events-none absolute inset-0 z-10 bg-gradient-to-b from-[#070b16] via-[#070b16]/95 to-[#070b16]/65"
              />
            </div>

            <div>
              <p className="home-meta mb-3" style={{ color: `rgb(${theme.accentRgb})` }}>
                {data.subtitle}
              </p>
              <h2 className="home-card-title-premium-sm mb-4 leading-tight text-white">
                {data.title}
              </h2>
              <p className="home-subnote leading-relaxed whitespace-pre-line text-gray-300/90">
                {data.description}
              </p>
            </div>
          </div>
        </div>
      </m.div>

      <div
        ref={cardsGridRef}
        className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6 2xl:grid-cols-3"
      >
        {data.subServices.map((sub, index) => {
          const CardIcon = ICON_BY_SLUG[data.slug] || Code2
          const cardNumber = String(index + 1).padStart(2, '0')
          const proofLine = getProofLine(data.benefits)

          return (
            <Link
              href={`/services/${data.slug}/${sub.slug}`}
              key={`${data.slug}/${sub.slug}`}
              data-testid={`service-card-${data.slug}-${sub.slug}`}
              onMouseEnter={() => prefetchRoute(`/services/${data.slug}/${sub.slug}`)}
              onFocus={() => prefetchRoute(`/services/${data.slug}/${sub.slug}`)}
              style={{ '--accent-rgb': theme.accentRgb } as CSSProperties}
              className="group relative flex h-full flex-col overflow-hidden rounded-[24px] border border-white/14 bg-gradient-to-b from-[#111a34]/72 via-[#0b1223]/92 to-[#090f1d]/98 [box-shadow:0_0_0px_transparent] backdrop-blur-xl transition-all duration-500 hover:-translate-y-1 hover:border-white/30 hover:[box-shadow:0_22px_60px_rgb(var(--accent-rgb)_/_0.22)]"
            >
              <div className="pointer-events-none absolute top-0 -left-1/3 h-full w-1/3 -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 blur-xl transition-all duration-700 group-hover:translate-x-[430%] group-hover:opacity-100" />
              <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[rgb(var(--accent-rgb))] to-transparent opacity-80" />
              <div className="pointer-events-none absolute -top-24 right-0 h-40 w-40 rounded-full bg-[rgb(var(--accent-rgb))]/12 blur-3xl transition-opacity duration-500 group-hover:opacity-100" />
              <div className="pointer-events-none absolute bottom-0 -left-12 h-32 w-32 rounded-full bg-[rgb(var(--accent-rgb))]/8 blur-2xl" />

              <div className="relative flex flex-1 flex-col p-5 md:p-6">
                <div className="mb-5 flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <span className="home-meta rounded-full border border-white/14 bg-white/[0.08] px-3 py-1.5 text-[10px] tracking-[0.14em] text-gray-300">
                      {data.title}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="bg-gradient-to-r from-white/75 to-white/25 bg-clip-text text-lg font-bold tracking-[0.08em] text-transparent">
                      {cardNumber}
                    </span>
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/14 bg-white/[0.08] text-gray-300 transition-colors duration-300 group-hover:bg-white/[0.14] group-hover:text-white">
                      <CardIcon className="h-4 w-4" />
                    </div>
                  </div>
                </div>

                <div
                  className="mb-5"
                  data-card-copy
                  style={maxCopyHeight > 0 ? { minHeight: `${maxCopyHeight}px` } : undefined}
                >
                  <div data-card-copy-inner>
                    <h4 className="home-card-title-sm mb-3 text-white">{sub.title}</h4>
                    <p className="home-subnote leading-relaxed text-gray-300/90">{sub.desc}</p>
                  </div>
                </div>

                <div className="mb-5 max-w-full">
                  <p
                    title={proofLine}
                    className="home-meta inline-flex max-w-full items-start rounded-2xl border border-white/14 bg-white/[0.08] px-3 py-1.5 text-[12px] leading-tight whitespace-normal text-gray-300"
                  >
                    <span className="block max-w-full leading-tight [overflow-wrap:anywhere] break-words whitespace-normal">
                      {proofLine}
                    </span>
                  </p>
                </div>

                {sub.price && (
                  <div className="mt-auto flex items-center justify-between border-t border-white/10 pt-3">
                    <span
                      className={`bg-gradient-to-r text-xl font-bold ${data.gradient} bg-clip-text text-transparent`}
                    >
                      {sub.price}
                    </span>
                    <ArrowRight className="h-5 w-5 text-gray-400 transition-all group-hover:translate-x-1 group-hover:text-white" />
                  </div>
                )}
              </div>
            </Link>
          )
        })}
      </div>

      <Link
        href={`/directions?tab=${data.slug}`}
        data-testid={`service-section-link-${data.slug}`}
        onMouseEnter={() => prefetchRoute(`/directions?tab=${data.slug}`)}
        onFocus={() => prefetchRoute(`/directions?tab=${data.slug}`)}
        className="home-meta mt-8 inline-flex items-center gap-3 text-gray-500 transition-colors hover:text-white"
      >
        Все услуги раздела <ArrowRight className="h-4 w-4" />
      </Link>
    </section>
  )
}

export default function ServicesClient({ services }: { services: ServiceDTO[] }) {
  const safeServices = useMemo(() => services || [], [services])
  const [activeFilter, setActiveFilter] = useState(safeServices[0]?.slug || '')
  const [hideStickyFilters, setHideStickyFilters] = useState(false)

  useEffect(() => {
    let lastY = window.scrollY
    let frame = 0

    const onScroll = () => {
      if (frame) return
      frame = window.requestAnimationFrame(() => {
        const currentY = window.scrollY
        const delta = currentY - lastY
        setHideStickyFilters((prev) => {
          const nextHidden =
            currentY < 150 ? false : delta > 4.5 ? true : delta < -2.8 ? false : prev
          return prev === nextHidden ? prev : nextHidden
        })
        lastY = currentY
        frame = 0
      })
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      if (frame) window.cancelAnimationFrame(frame)
      window.removeEventListener('scroll', onScroll)
    }
  }, [])

  useEffect(() => {
    if (!safeServices.length) return
    let frame = 0

    const evaluateScrollPosition = () => {
      const sections = safeServices
        .map((s) => ({ slug: s.slug, el: document.getElementById(`service-section-${s.slug}`) }))
        .filter((x) => x.el) as Array<{ slug: string; el: HTMLElement }>
      if (!sections.length) return

      const probeY = window.innerHeight * 0.28
      let best = sections[0].slug
      let minDistance = Number.POSITIVE_INFINITY

      sections.forEach(({ slug, el }) => {
        const rect = el.getBoundingClientRect()
        const distance = Math.abs(rect.top - probeY)
        if (distance < minDistance) {
          minDistance = distance
          best = slug
        }
      })

      setActiveFilter((prev) => (prev === best ? prev : best))
    }

    const onScroll = () => {
      if (frame) return
      frame = window.requestAnimationFrame(() => {
        frame = 0
        evaluateScrollPosition()
      })
    }

    evaluateScrollPosition()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll, { passive: true })

    return () => {
      if (frame) window.cancelAnimationFrame(frame)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [safeServices])

  if (!safeServices.length) return null

  return (
    <div className="relative min-h-screen pb-0 font-sans text-white selection:bg-blue-500/30 md:pb-20">
      <section className="hero-mobile-safe relative z-10 flex min-h-[100svh] flex-col items-center justify-start overflow-visible px-4 py-0 text-center md:justify-center">
        <div className="home-container relative z-10 mx-auto flex min-h-[100svh] max-w-5xl flex-col items-center justify-center pt-9 pb-2 max-[767px]:min-h-[calc(100svh-var(--nav-offset)-1rem)] max-[767px]:justify-start max-[767px]:pt-2 sm:pt-11 md:pt-16 md:pb-0 [@media(max-height:820px)]:md:pt-8 [@media(max-height:980px)]:md:pt-12">
          <m.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-7 inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-5 py-2.5 backdrop-blur-sm [@media(max-height:820px)]:md:mb-5"
          >
            <Sparkles className="h-4 w-4 text-blue-400" />
            <span className="home-meta text-gray-300">Наши решения</span>
          </m.div>

          <m.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="home-hero-title mb-6 text-center [@media(max-height:820px)]:md:mb-4 [@media(max-height:980px)]:md:mb-5"
          >
            <span className="block sm:whitespace-nowrap">Инструменты роста</span>
            <span className="block bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
              под вашу задачу
            </span>
          </m.h1>

          <m.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="home-hero-lead mx-auto mb-8 max-w-3xl text-center font-light text-gray-400 [@media(max-height:820px)]:md:mb-5 [@media(max-height:980px)]:md:mb-6"
          >
            Сайты, ИИ и маркетинг в одной системе. Подбираем направления под вашу цель и запускаем
            решения с измеримым результатом.
          </m.p>

          <m.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <button
              onClick={() => {
                const element = document.getElementById('services-grid')
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' })
                } else {
                  window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })
                }
              }}
              className="group relative flex items-center gap-3 rounded-full bg-white px-8 py-4 text-lg font-bold text-black transition-all duration-300 hover:shadow-[0_0_40px_rgba(255,255,255,0.3)] [@media(max-height:980px)]:md:px-7 [@media(max-height:980px)]:md:py-3 [@media(max-height:980px)]:md:text-base"
            >
              Выбрать услугу
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </button>
          </m.div>
        </div>
      </section>

      <div id="services-grid" className="home-container relative z-10">
        <m.div
          className={`sticky top-[calc(var(--nav-offset)+0.75rem)] z-30 mb-8 flex justify-center sm:top-[calc(var(--nav-offset)+1rem)] md:mb-12 ${
            hideStickyFilters ? 'pointer-events-none' : ''
          }`}
          initial={false}
          animate={
            hideStickyFilters
              ? { y: -18, opacity: 0, scale: 0.985, filter: 'blur(2px)' }
              : { y: 0, opacity: 1, scale: 1, filter: 'blur(0px)' }
          }
          transition={{ type: 'spring', stiffness: 360, damping: 34, mass: 0.75 }}
        >
          <div className="flex max-w-full flex-wrap justify-center gap-1.5 rounded-[24px] border border-white/10 bg-[#0c1220]/80 px-2 py-2 shadow-2xl backdrop-blur-xl md:gap-2 md:rounded-full md:p-2">
            {safeServices.map((item) => {
              const Icon = ICON_BY_SLUG[item.slug] || Code2
              const isActive = activeFilter === item.slug
              return (
                <button
                  key={item.slug}
                  onClick={() => {
                    setActiveFilter(item.slug)
                    const el = document.getElementById(`service-section-${item.slug}`)
                    if (!el) return
                    const top = el.getBoundingClientRect().top + window.scrollY - 190
                    window.scrollTo({ top, behavior: 'smooth' })
                  }}
                  className={`home-caption group relative inline-flex items-center gap-2 overflow-hidden rounded-full px-4 py-2.5 text-center leading-tight font-medium whitespace-nowrap transition-all duration-300 md:px-6 md:py-3 md:text-sm ${
                    isActive
                      ? 'border border-white/20 bg-white/12 text-white'
                      : 'border border-transparent text-gray-400 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <Icon
                    className={`h-3.5 w-3.5 ${isActive ? 'text-white' : 'text-gray-500 group-hover:text-gray-300'}`}
                  />
                  {item.title}
                </button>
              )
            })}
          </div>
        </m.div>

        {safeServices.map((data, i) => (
          <DirectionContent key={data.slug} data={data} isLast={i === safeServices.length - 1} />
        ))}
      </div>
    </div>
  )
}
